import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import OmButton from '../ui/OmButton';
import { useDispatch, useSelector } from 'react-redux';
import { downloadDocument } from '../../features/documents/documentSlice';
import { getDocumentTypeLabel, requiresStampValidation, getDocumentPreviewUrl } from '../../features/documents/documentService';
import { getStampValidationStatus, canValidateDocument } from '../../utils/stampValidationUtils';
import { selectUserRoles } from '../../features/auth/authSlice';
import { apiClient } from '../../utils/apiClient';
import StampValidationModal from './StampValidationModal';

function DocumentPreview({ document, onClose, onValidationSuccess }) {
  const dispatch = useDispatch();
  const userRoles = useSelector(selectUserRoles);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showValidationModal, setShowValidationModal] = useState(false);

  const isClaimsOfficer = userRoles.includes('Claims_Officer');
  const isApprover = userRoles.includes('Approver');
  const canValidateStamp = canValidateDocument(document, isClaimsOfficer, isApprover);

  // Determine if image can be previewed
  const isImage = document?.mimeType?.startsWith('image/');
  const isPDF = document?.mimeType === 'application/pdf';
  const isODF = document?.mimeType?.startsWith('application/vnd.oasis.opendocument.');

  // Fetch PDF as blob when document is a PDF
  useEffect(() => {
    if (!document || !isPDF) return;

    const fetchPdfBlob = async () => {
      setIsLoading(true);
      setIsError(false);
      setErrorMessage('');
      
      try {
        // Use the existing apiClient to get pre-signed download URL
        const response = await apiClient.get(`documents/${document.id}/download`);
        
        const { downloadUrl } = response.data;
        
        if (!downloadUrl) {
          throw new Error('No download URL provided by server');
        }

        // Fetch the actual PDF blob from pre-signed URL
        const fileResponse = await fetch(downloadUrl);
        
        if (!fileResponse.ok) {
          throw new Error(`Failed to download PDF: ${fileResponse.status} ${fileResponse.statusText}`);
        }

        const blob = await fileResponse.blob();
        
        // Verify it's actually a PDF
        if (!blob.type.includes('pdf') && !blob.type.includes('application/pdf')) {
          // Console log removed for production
        }
        
        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
        setIsError(false);
      } catch (error) {
        // Console log removed for production
        setPdfBlobUrl(null);
        setIsError(true);
        
        // Handle different types of errors
        if (error.response?.status === 401) {
          setErrorMessage('Authentication required. Please log in again.');
        } else if (error.response?.status === 403) {
          setErrorMessage('Access denied. You do not have permission to view this document.');
        } else if (error.response?.status === 404) {
          setErrorMessage('Document not found.');
        } else {
          setErrorMessage(error.message || 'Failed to load PDF preview');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPdfBlob();

    // Cleanup function to revoke blob URL
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [document, isPDF]); 

  // Fetch image preview URL when document is an image
  useEffect(() => {
    if (!document || !isImage) return;

    const fetchImagePreviewUrl = async () => {
      setIsLoading(true);
      setIsError(false);
      setErrorMessage('');
      
      try {
        console.log('Fetching image preview for document:', document.id, document.name);
        // Use the preview endpoint to get pre-signed URL
        const response = await apiClient.get(`documents/${document.id}/preview`);
        
        console.log('Preview API response:', response.data);
        const { previewUrl } = response.data;
        
        if (!previewUrl) {
          throw new Error('No preview URL provided by server');
        }

        console.log('Setting image preview URL:', previewUrl);
        setImagePreviewUrl(previewUrl);
        setIsError(false);
      } catch (error) {
        console.error('Error fetching image preview:', error);
        setIsError(true);
        setErrorMessage(error.response?.data?.error || error.message || 'Failed to load image preview');
      } finally {
        setIsLoading(false);
      }
    };

    fetchImagePreviewUrl();
  }, [document, isImage]);

  // Get preview URL using the document service
  const getPreviewUrl = () => {
    // For images, use the fetched preview URL
    if (isImage && imagePreviewUrl) {
      return imagePreviewUrl;
    }
    
    // For other files, try various fallback URLs
    return document?.url || document?.previewUrl || document?.file_url || document?.downloadUrl;
  };

  if (!document) return null;

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle download
  const handleDownload = async () => {
    setIsLoading(true);
    try {
      await dispatch(downloadDocument({ 
        documentId: document.id, 
        filename: document.name 
      })).unwrap();
    } catch (error) {
      // Console log removed for production
    } finally {
      setIsLoading(false);
    }
  };

  // Get file icon based on type
  const getFileIcon = () => {
    if (isPDF) {
      return (
        <svg className="h-16 w-16 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      );
    }
    if (document.mimeType?.includes('word') || document.name?.toLowerCase().includes('.doc')) {
      return (
        <svg className="h-16 w-16 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    if (document.mimeType?.includes('excel') || document.name?.toLowerCase().includes('.xls')) {
      return (
        <svg className="h-16 w-16 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    if (isODF || document.name?.toLowerCase().match(/\.(odt|ods|odp)$/)) {
      return (
        <svg className="h-16 w-16 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    // Default file icon
    return (
      <svg className="h-16 w-16 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
      </svg>
    );
  };

  // Get stamp validation status badge
  const getStampValidationBadge = () => {
    if (!requiresStampValidation(document?.type)) {
      return null;
    }

    const status = getStampValidationStatus(document);
    switch (status) {
      case 'VALID':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Stamp Valid
          </span>
        );
      case 'INVALID':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Stamp Invalid
          </span>
        );
      case 'PENDING':
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <svg className="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Validation Pending
          </span>
        );
    }
  };

  // Handle stamp validation
  const handleValidateStamp = () => {
    setShowValidationModal(true);
  };

  const handleValidationSuccess = () => {
    setShowValidationModal(false);
    if (onValidationSuccess) {
      onValidationSuccess();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto bg-carbon-black bg-opacity-60 pointer-events-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative top-12 mx-auto p-0 bg-crystal-white w-full max-w-6xl max-h-full overflow-hidden shadow-depth border border-steel/20 rounded-lg">
          {/* Header */}
          <div className="bg-deep-jungle text-crystal-white px-8 py-6 border-b-3 border-forest-fern">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-display font-bold text-crystal-white truncate mb-3">
                  {document.name}
                </h3>
                
                {/* Document metadata badges */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="badge bg-zesty-lime text-crystal-white border-0 px-3 py-1">
                    {getDocumentTypeLabel(document.type)}
                  </span>
                  
                  {requiresStampValidation(document.type) && (
                    <span className="badge bg-woodland-moss text-crystal-white border-0 px-3 py-1" title="Requires medical stamp validation">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Medical Document
                    </span>
                  )}
                  
                  {getStampValidationBadge()}
                </div>

                {/* File metadata */}
                <div className="flex flex-wrap items-center gap-6 text-sm text-silver-mist opacity-85">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    {formatFileSize(document.size)}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    {formatDate(document.createdAt)}
                  </span>
                </div>
                
                {/* Validation Details */}
                {requiresStampValidation(document.type) && document.stampValidatedBy && (
                  <div className="mt-4 p-4 bg-crystal-white bg-opacity-15 border border-silver-mist border-opacity-30">
                    <p className="text-sm text-crystal-white">
                      <span className="font-medium">Validated by:</span> {document.stampValidatedBy.username}
                      {document.stampValidatedAt && (
                        <span className="ml-3 opacity-85">
                          {formatDate(document.stampValidatedAt)}
                        </span>
                      )}
                    </p>
                    {document.stampValidationNotes && (
                      <p className="mt-2 text-sm text-silver-mist italic opacity-85">
                        "{document.stampValidationNotes}"
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="flex items-start gap-3 ml-6">
                {/* Validate Stamp Button - Only show if document needs validation */}
                {canValidateStamp && getStampValidationStatus(document) !== 'VALID' && (
                  <OmButton
                    onClick={handleValidateStamp}
                    buttonType="filled-button"
                    buttonSize="medium"
                    leadingIcon="task_alt"
                  >
                    Validate Stamp
                  </OmButton>
                )}

                <OmButton
                  onClick={handleDownload}
                  disabled={isLoading}
                  buttonType="outlined-button"
                  buttonSize="medium"
                  leadingIcon="file_download"
                >
                  {isLoading ? 'Preparing…' : 'Download'}
                </OmButton>
                
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-10 h-10 text-silver-mist hover:text-crystal-white transition-colors focus:outline-none focus:ring-2 focus:ring-crystal-white focus:ring-offset-2 focus:ring-offset-deep-jungle"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Content with watermark overlay */}
          <div className="relative bg-silver-mist" style={{ minHeight: '600px' }}>
            {/* Watermark for invalid/missing stamps */}
            {requiresStampValidation(document.type) && getStampValidationStatus(document) === 'INVALID' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="transform -rotate-45 bg-error text-crystal-white px-12 py-4 text-3xl font-bold opacity-15 border-4 border-error">
                  INVALID STAMP
                </div>
              </div>
            )}
            {requiresStampValidation(document.type) && getStampValidationStatus(document) === 'PENDING' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="transform -rotate-45 bg-warning text-crystal-white px-12 py-4 text-3xl font-bold opacity-15 border-4 border-warning">
                  PENDING VALIDATION
                </div>
              </div>
            )}

            <div className="p-8 h-full">
              {isImage ? (
                <div className="h-full flex items-center justify-center">
                  {isLoading ? (
                    <div className="h-96 flex flex-col items-center justify-center bg-crystal-white border border-steel border-opacity-30">
                      <div className="w-12 h-12 border-4 border-silver-mist border-t-fresh-leaf animate-spin mb-6"></div>
                      <p className="text-graphite font-medium">Loading image preview...</p>
                      <p className="text-steel text-sm mt-2">Please wait while we prepare the document</p>
                    </div>
                  ) : isError ? (
                    <div className="h-96 flex flex-col items-center justify-center text-center bg-crystal-white border border-steel border-opacity-30">
                      <svg className="h-16 w-16 text-steel mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <h4 className="text-lg font-medium text-graphite mb-2">Image Preview Unavailable</h4>
                      <p className="mt-2 text-error font-medium">{errorMessage}</p>
                      <p className="text-steel text-sm mt-1">Use the download button to access the document</p>
                    </div>
                  ) : imagePreviewUrl ? (
                    <div className="max-w-full max-h-full border border-steel border-opacity-30">
                      <img
                        src={imagePreviewUrl}
                        alt={document.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden h-96 flex-col items-center justify-center text-center border border-steel border-opacity-30 bg-crystal-white">
                        <svg className="h-16 w-16 text-steel mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <h4 className="text-lg font-medium text-graphite mb-2">Image Preview Unavailable</h4>
                        <p className="text-steel">Download the file to view the image</p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-96 flex flex-col items-center justify-center text-center bg-crystal-white border border-steel border-opacity-30">
                      <svg className="h-16 w-16 text-steel mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <h4 className="text-lg font-medium text-graphite mb-2">Image Preview Unavailable</h4>
                      <p className="text-steel">Unable to load preview URL</p>
                    </div>
                  )}
                </div>
              ) : isODF ? (
                <div className="h-96 flex flex-col items-center justify-center text-center bg-crystal-white border border-steel border-opacity-30">
                  {getFileIcon()}
                  <h4 className="mt-6 text-lg font-medium text-graphite">OpenDocument Format</h4>
                  <p className="mt-2 text-steel">
                    Preview not supported for ODF files
                  </p>
                  <p className="text-steel text-sm mt-1">
                    Use the download button to access the document
                  </p>
                </div>
              ) : isPDF ? (
                <div className="h-full">
                  {isLoading ? (
                    <div className="h-96 flex flex-col items-center justify-center bg-crystal-white border border-steel border-opacity-30">
                      <div className="w-12 h-12 border-4 border-silver-mist border-t-fresh-leaf animate-spin mb-6"></div>
                      <p className="text-graphite font-medium">Loading PDF preview...</p>
                      <p className="text-steel text-sm mt-2">Please wait while we prepare the document</p>
                    </div>
                  ) : isError ? (
                    <div className="h-96 flex flex-col items-center justify-center text-center bg-crystal-white border border-steel border-opacity-30">
                      {getFileIcon()}
                      <h4 className="mt-6 text-lg font-medium text-graphite">PDF Preview Unavailable</h4>
                      <p className="mt-2 text-error font-medium">{errorMessage}</p>
                      <p className="text-steel text-sm mt-1">
                        Use the download button to access the document
                      </p>
                    </div>
                  ) : pdfBlobUrl ? (
                    <iframe
                      src={pdfBlobUrl}
                      title={document.name}
                      className="w-full h-full border border-steel border-opacity-30"
                      style={{ minHeight: '500px' }}
                      onError={() => {
                        setIsError(true);
                        setErrorMessage('PDF preview failed to load in iframe');
                      }}
                    />
                  ) : (
                    <div className="h-96 flex flex-col items-center justify-center text-center bg-crystal-white border border-steel border-opacity-30">
                      {getFileIcon()}
                      <h4 className="mt-6 text-lg font-medium text-graphite">PDF Document</h4>
                      <p className="mt-2 text-steel">
                        Preview is not available for this document
                      </p>
                      <p className="text-steel text-sm mt-1">
                        Use the download button to access the document
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-96 flex flex-col items-center justify-center text-center bg-crystal-white border border-steel border-opacity-30">
                  {getFileIcon()}
                  <h4 className="mt-6 text-lg font-medium text-graphite">
                    {document.mimeType || 'Document File'}
                  </h4>
                  <p className="mt-2 text-steel">
                    Preview not supported for this file type
                  </p>
                  <p className="text-steel text-sm mt-1">
                    Use the download button to access the document
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-crystal-white border-t border-silver-mist px-8 py-6">
            <div className="flex justify-end">
              <OmButton
                onClick={onClose}
                buttonType="filled-button"
                buttonSize="large"
              >
                Close Preview
              </OmButton>
            </div>
          </div>
        </div>
      </div>

      {/* Stamp Validation Modal */}
      {showValidationModal && (
        <StampValidationModal
          document={document}
          onClose={() => setShowValidationModal(false)}
          onSuccess={handleValidationSuccess}
        />
      )}
    </div>,
    window.document && window.document.body ? window.document.body : null
  );
}

export default DocumentPreview;
